/**
 * DED-1141: Incident Linked Documents Section
 * A specialised component for linking supporting documents to Incidents
 * Documents are created with specific metadata:
 * - Document Type: Incident
 * - Last Review Date: Today
 * - Next Review Date: 3 months from today
 * - Data Classification: Business Confidential
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, ExternalLink, Trash2, Link as LinkIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useTenancy } from '@/contexts/TenancyContext';
import { toast } from 'sonner';
import { getDocument, saveDocument } from '@/services/documentsService';
import { renderDate } from '@shared/renderDate';
import { DocumentsCombobox, DocumentsProvider } from '@/components/ui/DocumentsCombobox';
import { Label } from '@/components/ui/label';

interface LinkedDocument {
  docID: string;
  fileName: string;
  displayName?: string;
  documentType?: string;
  nextReviewDate?: Date | string;
  lastReviewDate?: Date | string;
}

interface IncidentLinkedDocumentsSectionProps {
  /** Current linked document IDs */
  linkedDocuments: string[];
  /** Callback when documents change */
  onDocumentsChange: (docIds: string[]) => void;
  /** Controls to inherit/append to linked documents */
  inheritedControls?: string[];
  /** Title for the incident (used in document descriptions) */
  incidentTitle?: string;
  /** Owner of the incident to inherit to documents */
  incidentOwner?: string;
  /** Whether the section is read-only */
  isReadOnly?: boolean;
}

export const IncidentLinkedDocumentsSection: React.FC<IncidentLinkedDocumentsSectionProps> = ({
  linkedDocuments = [],
  onDocumentsChange,
  inheritedControls = [],
  incidentTitle,
  incidentOwner,
  isReadOnly = false,
}) => {
  const { activeTenancy } = useTenancy();
  const [documents, setDocuments] = useState<LinkedDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedDocsToLink, setSelectedDocsToLink] = useState<string[]>([]);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [docToUnlink, setDocToUnlink] = useState<string | null>(null);

  // Fetch document details for display
  const fetchDocuments = useCallback(async (docIdsToFetch?: string[]) => {
    const idsToFetch = docIdsToFetch || linkedDocuments;

    if (!activeTenancy?.id || idsToFetch.length === 0) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const fetchedDocs: LinkedDocument[] = [];

      for (const docId of idsToFetch) {
        try {
          const docData = await getDocument(docId, activeTenancy.id);
          if (docData) {
            fetchedDocs.push({
              docID: docId,
              fileName: docData.fileName || (docData as any).title || 'Untitled',
              displayName: (docData as any).displayName || (docData as any).title || docData.fileName,
              documentType: docData.documentType,
              nextReviewDate: docData.nextReviewDate as any,
              lastReviewDate: docData.lastReviewDate as any,
            });
          }
        } catch (err) {
          console.error(`[IncidentLinkedDocumentsSection] Error fetching document ${docId}:`, err);
        }
      }

      setDocuments(fetchedDocs);
    } catch (error) {
      console.error('[IncidentLinkedDocumentsSection] Error fetching linked documents:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, linkedDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // DED-1141: Create document with incident-specific metadata
  const handleCreateDocument = () => {
    const today = new Date();
    // Next review date is 3 months from today
    const nextReviewDate = new Date(today);
    nextReviewDate.setMonth(nextReviewDate.getMonth() + 3);

    const prefillData = {
      // DED-1141: Incident-specific document metadata
      documentType: 'Incident',
      classification: 'Business Confidential',
      status: 'Draft',
      title: incidentTitle ? `Incident: ${incidentTitle}` : 'Incident Supporting Document',
      description: incidentTitle
        ? `Supporting document for incident: ${incidentTitle}`
        : 'Supporting document for incident',
      dateAdded: renderDate(today),
      lastReviewDate: today.toISOString(),
      nextReviewDate: nextReviewDate.toISOString(),
      // Inherit controls and owner from the parent incident
      controls: inheritedControls,
      owner: incidentOwner,
    };

    // Store prefill data and open document editor in new tab
    sessionStorage.setItem('documentPrefillData', JSON.stringify(prefillData));
    sessionStorage.setItem('documentPrefillCallback', JSON.stringify({
      type: 'entityLinkedDocument',
      entityType: 'Incident',
    }));
    window.open('/documentdetail/new', '_blank');
    
    toast.info('Document editor opened in new tab. Link the document after saving.');
  };

  // Handle linking existing documents
  const handleLinkDocuments = async () => {
    if (selectedDocsToLink.length === 0) return;

    try {
      // DED-1141: Update each linked document with incident-specific metadata
      if (activeTenancy?.id) {
        const today = new Date();
        const nextReviewDate = new Date(today);
        nextReviewDate.setMonth(nextReviewDate.getMonth() + 3);

        for (const docId of selectedDocsToLink) {
          try {
            const docData = await getDocument(docId, activeTenancy.id);
            if (docData) {
              const existingControls = (docData as any).controls || [];
              // Append controls (don't duplicate)
              const mergedControls = [...new Set([...existingControls, ...inheritedControls])];
              
              // Update document with incident-specific metadata
              await saveDocument({
                ...docData,
                documentType: 'Incident',
                classification: 'Business Confidential',
                lastReviewDate: today.toISOString(),
                nextReviewDate: nextReviewDate.toISOString(),
                controls: mergedControls,
                owner: incidentOwner || docData.owner,
              } as any, null, activeTenancy.id);
            }
          } catch (err) {
            console.error(`[IncidentLinkedDocumentsSection] Error updating document ${docId}:`, err);
          }
        }
      }

      const newLinkedDocs = [...linkedDocuments, ...selectedDocsToLink];
      onDocumentsChange(newLinkedDocs);
      await fetchDocuments(newLinkedDocs);
      toast.success(`Linked ${selectedDocsToLink.length} document(s)`);
      setShowLinkDialog(false);
      setSelectedDocsToLink([]);
    } catch (error) {
      console.error('[IncidentLinkedDocumentsSection] Error linking documents:', error);
      toast.error('Failed to link documents');
    }
  };

  // Handle unlinking a document
  const handleUnlinkDocument = async (docId: string) => {
    try {
      const newLinkedDocs = linkedDocuments.filter(id => id !== docId);
      onDocumentsChange(newLinkedDocs);
      toast.success('Document unlinked');
      setShowUnlinkDialog(false);
      setDocToUnlink(null);
      await fetchDocuments(newLinkedDocs);
    } catch (error) {
      console.error('[IncidentLinkedDocumentsSection] Error unlinking document:', error);
      toast.error('Failed to unlink document');
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Supporting Document
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {documents.length}
              </Badge>
            )}
          </Label>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(true)}
              >
                <LinkIcon className="h-3 w-3 mr-1" />
                Link Existing
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCreateDocument}
              >
                <Plus className="h-3 w-3 mr-1" />
                Create New
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-4 border rounded-lg border-dashed">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-xs text-muted-foreground">No supporting document</p>
            {!isReadOnly && (
              <p className="text-xs text-muted-foreground mt-1">
                Click "Create New" or "Link Existing" to add a document
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {documents.map(doc => (
              <div
                key={doc.docID}
                className="flex items-center justify-between p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-xs truncate">
                      {doc.displayName || doc.fileName}
                    </p>
                    {doc.documentType && (
                      <Badge variant="outline" className="text-xs mt-0.5">
                        {doc.documentType}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => window.open(`/documentdetail/${doc.docID}`, '_blank')}
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  {!isReadOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => {
                        setDocToUnlink(doc.docID);
                        setShowUnlinkDialog(true);
                      }}
                      title="Unlink document"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Link Existing Documents Dialog */}
      <Dialog
        open={showLinkDialog}
        onOpenChange={open => {
          setShowLinkDialog(open);
          if (!open) setSelectedDocsToLink([]);
        }}
      >
        <DialogContent className="sm:max-w-[500px] bg-card overflow-visible">
          <DialogHeader>
            <DialogTitle>Link Existing Documents</DialogTitle>
            <DialogDescription>
              Select documents from your document library to link to this incident.
              <span className="block mt-2 text-xs">
                Documents will be updated with:
                {incidentOwner && <span className="block">• Owner: {incidentOwner}</span>}
                <span className="block">• Type: Incident</span>
                <span className="block">• Classification: Business Confidential</span>
                {inheritedControls.length > 0 && <span className="block">• {inheritedControls.length} control(s)</span>}
                <span className="block">• Last review date: Today</span>
                <span className="block">• Next review date: 3 months from today</span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 min-h-[120px]">
            <DocumentsProvider>
              <DocumentsCombobox
                selected={selectedDocsToLink}
                setSelected={setSelectedDocsToLink}
                excludeIds={linkedDocuments}
                placeholder="Search and select documents..."
                fullWidth
              />
            </DocumentsProvider>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setSelectedDocsToLink([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleLinkDocuments}
              disabled={selectedDocsToLink.length === 0}
            >
              Link {selectedDocsToLink.length > 0 ? `(${selectedDocsToLink.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Unlink Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this document from the incident? The document will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowUnlinkDialog(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => docToUnlink && handleUnlinkDocument(docToUnlink)}
            >
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncidentLinkedDocumentsSection;
